/**
 * ============================================================
 * KEJJA OS — src/hooks/useAuth.tsx
 * Authentication context + useAuth() hook.
 * ============================================================
 *
 * Provides the current KEJJA user (from our backend, not Firebase)
 * and the sign-in / sign-up / logout actions.
 *
 * Firebase is used purely as a token provider — we don't trust
 * Firebase state directly. The source of truth is our backend.
 * ============================================================
 */

import {
  createContext, useCallback, useContext, useEffect, useState,
  type ReactNode,
} from 'react';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile as fbUpdateProfile,
  signOut as firebaseSignOut,
} from 'firebase/auth';

import { firebaseAuth, googleProvider } from '@/lib/firebase';
import { authApi, ApiError, type KejjaUser } from '@/lib/api';

// ------------------------------------------------------------
// Context shape
// ------------------------------------------------------------

interface AuthContextValue {
  user: KejjaUser | null;
  isLoading: boolean;              // initial bootstrap loading
  isAuthenticating: boolean;        // an auth action is in flight
  error: string | null;

  signInWithGoogle: () => Promise<void>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string, displayName: string) => Promise<void>;
  logout: () => Promise<void>;

  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// ------------------------------------------------------------
// Provider
// ------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<KejjaUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => setError(null), []);

  // --- Bootstrap: on mount, check if we already have a valid session ---
  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const me = await authApi.me();
        if (!cancelled) setUser(me);
      } catch (err) {
        // 401 = not signed in. That's fine.
        if (err instanceof ApiError && err.status === 401) {
          if (!cancelled) setUser(null);
        } else {
          console.error('Auth bootstrap failed:', err);
          if (!cancelled) setUser(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    bootstrap();
    return () => { cancelled = true; };
  }, []);

  // --- Shared token-exchange step ---
  // After we have a Firebase user, trade the ID token for our own session.
  const exchangeFirebaseForSession = useCallback(async () => {
    const fbUser = firebaseAuth.currentUser;
    if (!fbUser) throw new Error('No Firebase user after sign-in');

    const idToken = await fbUser.getIdToken(/* forceRefresh */ true);
    const { user: kejjaUser } = await authApi.signInWithFirebase(idToken);
    setUser(kejjaUser);
  }, []);

  // --- Actions ---

  const signInWithGoogle = useCallback(async () => {
    setIsAuthenticating(true);
    setError(null);
    try {
      await signInWithPopup(firebaseAuth, googleProvider);
      await exchangeFirebaseForSession();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setError(friendlyAuthError(message));
      // Clean up any partial Firebase state
      await firebaseSignOut(firebaseAuth).catch(() => {});
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, [exchangeFirebaseForSession]);

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    setIsAuthenticating(true);
    setError(null);
    try {
      await signInWithEmailAndPassword(firebaseAuth, email.trim(), password);
      await exchangeFirebaseForSession();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Sign-in failed';
      setError(friendlyAuthError(message));
      await firebaseSignOut(firebaseAuth).catch(() => {});
      throw err;
    } finally {
      setIsAuthenticating(false);
    }
  }, [exchangeFirebaseForSession]);

  const signUpWithEmail = useCallback(
    async (email: string, password: string, displayName: string) => {
      setIsAuthenticating(true);
      setError(null);
      try {
        const cred = await createUserWithEmailAndPassword(
          firebaseAuth, email.trim(), password,
        );
        // Set display name on Firebase profile so it propagates to our /session endpoint
        if (cred.user && displayName) {
          await fbUpdateProfile(cred.user, { displayName: displayName.trim() });
          await cred.user.reload();
        }
        await exchangeFirebaseForSession();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Sign-up failed';
        setError(friendlyAuthError(message));
        await firebaseSignOut(firebaseAuth).catch(() => {});
        throw err;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [exchangeFirebaseForSession],
  );

  const logout = useCallback(async () => {
    setIsAuthenticating(true);
    try {
      await authApi.logout().catch(() => {});
      await firebaseSignOut(firebaseAuth).catch(() => {});
      setUser(null);
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user, isLoading, isAuthenticating, error,
        signInWithGoogle, signInWithEmail, signUpWithEmail, logout,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ------------------------------------------------------------
// Hook
// ------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}

// ------------------------------------------------------------
// Translate Firebase error codes to human-friendly messages
// ------------------------------------------------------------

function friendlyAuthError(raw: string): string {
  if (raw.includes('auth/invalid-credential')) {
    return 'That email and password combination doesn’t match our records.';
  }
  if (raw.includes('auth/email-already-in-use')) {
    return 'An account with this email already exists. Try signing in instead.';
  }
  if (raw.includes('auth/weak-password')) {
    return 'Your password must be at least 6 characters.';
  }
  if (raw.includes('auth/invalid-email')) {
    return 'That doesn’t look like a valid email address.';
  }
  if (raw.includes('auth/popup-closed-by-user')) {
    return 'You closed the sign-in window. Try again when ready.';
  }
  if (raw.includes('auth/popup-blocked')) {
    return 'Your browser blocked the sign-in popup. Allow popups for this site.';
  }
  if (raw.includes('auth/network-request-failed')) {
    return 'Network error. Please check your internet connection and try again.';
  }
  if (raw.includes('auth/too-many-requests')) {
    return 'Too many attempts. Please wait a minute before trying again.';
  }
  return raw.replace(/^Firebase: Error \(auth\//, '').replace(/\)\.?$/, '').trim() || 'Sign-in failed';
}

